import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../../contexts/AuthContext';
import {
  BriefcaseIcon,
  MapPinIcon,
  AcademicCapIcon,
  CurrencyDollarIcon,
  ClockIcon,
  ArrowLeftIcon,
} from '@heroicons/react/24/outline';

const JobPostDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [job, setJob] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => {
    const fetchDetail = async () => {
      try {
        setLoading(true);
        const res = await axios.get(`/api/job-posts/jobs/${id}`);
        setJob(res.data.job);
        // Update lastAccessedAt when job is opened
        await axios.patch(`/api/job-posts/jobs/${id}`, {
          lastAccessedAt: new Date().toISOString(),
        });
      } catch (e) {
        if (e.response?.status === 404) {
          navigate('/dashboard/job-posts', { replace: true });
          return;
        }
        setError(e.response?.data?.message || 'Failed to load job');
      } finally {
        setLoading(false);
      }
    };
    fetchDetail();
  }, [id]);

  if (loading) {
    return (
      <div className="p-6">
        <div className="max-w-4xl mx-auto text-center text-gray-600">Loading job...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6">
        <div className="max-w-4xl mx-auto text-red-600">{error}</div>
      </div>
    );
  }

  if (!job) {
    return null;
  }

  return (
    <div className="p-6">
      <div className="max-w-4xl mx-auto space-y-6">
        <button
          onClick={() => navigate(-1)}
          className="inline-flex items-center text-sm text-gray-600 hover:text-gray-900"
        >
          <ArrowLeftIcon className="h-4 w-4 mr-1" /> Back
        </button>

        <div className="flex items-center space-x-3">
          <BriefcaseIcon className="h-8 w-8 text-indigo-600" />
          <div>
            <h1 className="text-2xl font-bold text-gray-900">{job.title}</h1>
            <p className="text-sm text-gray-500">
              Status: {job.status === 0 || job.status === 'closed' ? 'Closed' : 'Active'}
            </p>
          </div>
        </div>

        <div className="bg-white rounded-lg border border-gray-200 p-6 space-y-6">
          <div>
            <h2 className="text-sm font-semibold text-gray-700 mb-2">Description</h2>
            <p className="text-gray-800 whitespace-pre-line">{job.description}</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {(Array.isArray(job.location) ? job.location.length > 0 : job.location) ||
            job.locationType ? (
              <div className="flex items-center text-sm text-gray-700">
                <MapPinIcon className="h-4 w-4 mr-2" />
                {Array.isArray(job.location) ? job.location.join(', ') : job.location || '—'}{' '}
                {job.locationType ? `(${job.locationType})` : ''}
              </div>
            ) : null}
            {(job.experienceMin !== undefined || job.experienceMax !== undefined) && (
              <div className="flex items-center text-sm text-gray-700">
                <AcademicCapIcon className="h-4 w-4 mr-2" />
                {job.experienceMin ?? '0'}-{job.experienceMax ?? '0'} years
              </div>
            )}
            {(job.salaryMin !== undefined || job.salaryMax !== undefined) && (
              <div className="flex items-center text-sm text-gray-700">
                <CurrencyDollarIcon className="h-4 w-4 mr-2" />
                {job.salaryCurrency} {job.salaryMin ?? 0}-{job.salaryMax ?? 0}
              </div>
            )}
            {job.noticePeriod && (
              <div className="flex items-center text-sm text-gray-700">
                <ClockIcon className="h-4 w-4 mr-2" />
                Notice Period: {job.noticePeriod}
              </div>
            )}
            {/* employmentType removed in slim schema */}
          </div>

          {Array.isArray(job.skills) && job.skills.length > 0 && (
            <div>
              <h2 className="text-sm font-semibold text-gray-700 mb-2">Skills</h2>
              <div className="flex flex-wrap gap-2">
                {job.skills.map((skill, i) => (
                  <span
                    key={i}
                    className="px-2 py-1 bg-indigo-50 text-indigo-700 text-xs font-medium rounded-full"
                  >
                    {skill}
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default JobPostDetail;
